import { IDiagnosticTemplate } from "../models/IDiagnosticTemplate";

export const DIAGNOSTIC_TEMPLATE_CATEGORIES = [
  { name: "Change Management", color: "#5b6cd9" },
  { name: "Agile & Product", color: "#0e9aa7" },
  { name: "PMO & PPM", color: "#d98a2b" },
  { name: "Technology & Digital", color: "#8b6ee0" },
  { name: "Life Sciences", color: "#3fbf7f" },
] as const;

export const DIAGNOSTIC_TEMPLATES: IDiagnosticTemplate[] = [
  {
    id: "change-readiness",
    name: "Change Readiness",
    category: "Change Management",
    description:
      "Assesses organisational readiness for change across leadership alignment, stakeholder impact, and workforce resilience dimensions.",
    whenToUse:
      "When a client is preparing to launch a transformation and needs a baseline view of readiness across key change levers.",
    questionBankUrl: "https://northhighland.sharepoint.com",
    guideUrl: "https://northhighland.sharepoint.com",
  },
  {
    id: "change-pulse",
    name: "Change Pulse",
    category: "Change Management",
    description:
      "A lightweight sentiment tracker that measures how employees are experiencing change in real time — surfacing momentum or resistance early.",
    whenToUse:
      "During an active transformation when leaders need regular pulse data to course-correct communications and support.",
    questionBankUrl: "https://northhighland.sharepoint.com",
    guideUrl: "https://northhighland.sharepoint.com",
  },
  {
    id: "sustaining-change",
    name: "Sustaining Change",
    category: "Change Management",
    description:
      "Evaluates whether changes are embedding into culture, process, and behaviour after go-live — identifying sustainability risks before they become reversals.",
    whenToUse:
      "Three to six months post-implementation when the business wants to confirm adoption is holding.",
    questionBankUrl: "https://northhighland.sharepoint.com",
    guideUrl: "https://northhighland.sharepoint.com",
  },
  {
    id: "tdna",
    name: "Transformation Diagnostic (TDNA)",
    category: "Change Management",
    description:
      "A comprehensive organisational diagnostic covering strategy, culture, capability, and execution readiness for large-scale transformation programmes.",
    whenToUse:
      "At the outset of a major enterprise transformation to establish a holistic baseline and prioritise interventions.",
    questionBankUrl: "https://northhighland.sharepoint.com",
    guideUrl: "https://northhighland.sharepoint.com",
  },
  {
    id: "agile-maturity",
    name: "Agile Maturity",
    category: "Agile & Product",
    description:
      "A suite of proprietary diagnostics covering Agile Culture, Squad, PO & SM, and Team of Agile Teams — giving a clear picture of current-state agile maturity and scaling readiness at both team and organisation level. Identifies gaps in culture, leadership, ways of working, and organisational design.",
    whenToUse:
      "When a client needs to understand their agile capability gaps before a scaling or transformation effort.",
    questionBankUrl:
      "https://northhighland.sharepoint.com/:x:/r/sites/hubProductManagement/MARSDocuments/Question%20Banks%20-%20NH%20Diagnostic%20IQ/Agile-Maturity-Diagnostics.xlsx?d=w65ac6adef5574a5cae43e5b75f034ea4&csf=1&web=1&e=cwGYQ5",
    guideUrl:
      "https://northhighland.sharepoint.com/:b:/r/sites/0020/Shared%20Documents/Maturity%20Assessments/MARS%20Hub/MARS%20Templates%20Hub/MARS%20reports%20samples%20(pdfs)/TIAA_Agile%20Change%20Assessment.pdf?csf=1&web=1&e=AEDhGr",
  },
  {
    id: "enterprise-p2p",
    name: "Enterprise P2P (Project to Product)",
    category: "Agile & Product",
    description:
      "Evaluates an organisation's shift from project-centric to product-centric operating models, assessing funding flows, team topologies, and value stream alignment.",
    whenToUse:
      "When a client is considering or mid-way through a project-to-product operating model transition.",
    questionBankUrl: "https://northhighland.sharepoint.com",
    guideUrl: "https://northhighland.sharepoint.com",
  },
  {
    id: "pmo-maturity",
    name: "PMO Maturity",
    category: "PMO & PPM",
    description:
      "Benchmarks PMO capability across governance, delivery, reporting, and strategic alignment dimensions using the NH PMO maturity framework.",
    whenToUse:
      "When a client wants to understand where their PMO sits on the maturity curve and what to prioritise next.",
    questionBankUrl: "https://northhighland.sharepoint.com",
    guideUrl: "https://northhighland.sharepoint.com",
  },
  {
    id: "program-maturity",
    name: "Program Maturity",
    category: "PMO & PPM",
    description:
      "Assesses the maturity of programme management practices including benefits realisation, dependency management, and stakeholder engagement.",
    whenToUse:
      "Before a large programme kicks off or when an in-flight programme is experiencing delivery challenges.",
    questionBankUrl: "https://northhighland.sharepoint.com",
    guideUrl: "https://northhighland.sharepoint.com",
  },
  {
    id: "ppm-capability",
    name: "PPM Capability Maturity",
    category: "PMO & PPM",
    description:
      "Provides a structured view of portfolio, programme, and project management capability maturity — from ad hoc to optimised — across people, process, and tooling.",
    whenToUse:
      "When a client needs an end-to-end view of PPM capability to inform a transformation roadmap.",
    questionBankUrl: "https://northhighland.sharepoint.com",
    guideUrl: "https://northhighland.sharepoint.com",
  },
  {
    id: "ai-maturity",
    name: "AI Maturity",
    category: "Technology & Digital",
    description:
      "Evaluates an organisation's readiness and capability across AI strategy, data foundations, talent, governance, and adoption — from experimentation to scaled deployment.",
    whenToUse:
      "When a client is developing an AI strategy or wants to benchmark their current state before investing in AI initiatives.",
    questionBankUrl: "https://northhighland.sharepoint.com",
    guideUrl: "https://northhighland.sharepoint.com",
  },
  {
    id: "digital-maturity",
    name: "Digital Maturity",
    category: "Technology & Digital",
    description:
      "Assesses digital capability across customer experience, operations, technology, and culture — identifying where to invest to accelerate digital transformation.",
    whenToUse:
      "At the start of a digital transformation engagement to prioritise capability-building investments.",
    questionBankUrl: "https://northhighland.sharepoint.com",
    guideUrl: "https://northhighland.sharepoint.com",
  },
  {
    id: "information-management",
    name: "Information Management",
    category: "Technology & Digital",
    description:
      "Evaluates data governance, information architecture, and records management maturity — identifying risks and opportunities in how the organisation manages its information assets.",
    whenToUse:
      "When a client is dealing with data quality, compliance, or information governance challenges.",
    questionBankUrl: "https://northhighland.sharepoint.com",
    guideUrl: "https://northhighland.sharepoint.com",
  },
  {
    id: "clinic-readiness",
    name: "Clinic Readiness",
    category: "Life Sciences",
    description:
      "Assesses clinical site and organisational readiness for trials or new care pathways — covering staffing, processes, technology, and regulatory compliance.",
    whenToUse:
      "Prior to launching a clinical trial or implementing a new care pathway at a healthcare organisation.",
    questionBankUrl: "https://northhighland.sharepoint.com",
    guideUrl: "https://northhighland.sharepoint.com",
  },
];
